"use client";
import { createTheme } from "@mui/material/styles";

function getTheme() {
  const keyColor = process.env.NEXT_PUBLIC_COMPANY_KEY_COLOR || "#6366f1";

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
      },
    },
    shape: {
      borderRadius: 14,
    },
    palette: {
      primary: {
        main: "#4f46e5",
        light: "#818cf8",
        dark: "#3730a3",
      },
      secondary: {
        main: keyColor,
      },
      error: {
        main: "#ef4444",   // red-500
        light: "#fca5a5", // red-300
        dark: "#b91c1c",  // red-700
      },
    },
    components: {
      MuiFormControl: {
        styleOverrides: {
          root: {
            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink) + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend":
              {
                maxWidth: "0.01px",
                visibility: "hidden",
              },
            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink) + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend > span":
              {
                display: "none",
              },
            "& .MuiInputLabel-root.MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend":
              {
                visibility: "visible",
              },
            "& .MuiInputLabel-root.MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend > span":
              {
                display: "inline-block",
              },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: "#fff",
            borderRadius: theme.shape.borderRadius,
            // padding: "0.35rem",
            height: 42,
          }),
          grouped: ({ theme }) => ({
            textTransform: "none",
            fontWeight: 500,
            letterSpacing: "0.02em",
            fontSize: "0.92rem",
            border: "1.5px solid rgba(99,102,241,0.35)",
            color: theme.palette.primary.dark,
            padding: "0.55rem 1.35rem",
            "&:hover": {
              backgroundColor: "rgba(99,102,241,0.08)",
            },
            "&.Mui-selected": {
              fontWeight: 600,
              color: theme.palette.primary.dark,
              backgroundColor: "rgba(99,102,241,0.16)",
              borderColor: "rgba(79,70,229,0.45)",
              "&:hover": {
                backgroundColor: "rgba(79,70,229,0.24)",
              },
            },
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontFamily:
              '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 500,
            letterSpacing: "0.02em",
            textTransform: "none",
            minHeight: 42,
            lineHeight: 1,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            letterSpacing: "0.02em",
            fontSize: "0.95rem",
            lineHeight: 1.2,
            fontFamily:
              '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
            padding: "0.65rem 1.4rem",
            borderRadius: 14,
            boxShadow: "none",
            transition: "all 0.18s ease",
            minHeight: 42,
            whiteSpace: "nowrap",
          },
          sizeSmall: {
            padding: "0.5rem 1.1rem",
            fontSize: "0.85rem",
            minHeight: 42,
          },
          sizeLarge: {
            padding: "0.85rem 1.65rem",
            fontSize: "1.05rem",
            minHeight: 42,
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: "#4f46e5",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#4338ca",
            },
            "&:active": {
              backgroundColor: "#3730a3",
            },
          },
          outlined: {
            padding: "calc(0.65rem - 1px) calc(1.4rem - 1px)",
            borderWidth: 1.5,
            borderColor: "rgba(79,70,229,0.35)",
            color: "#3730a3",
            "&:hover": {
              borderWidth: 1.5,
              borderColor: "rgba(79,70,229,0.55)",
              backgroundColor: "rgba(99,102,241,0.08)",
            },
          },
          outlinedError: ({ theme }) => ({
            padding: "calc(0.65rem - 1.5px) calc(1.4rem - 1.5px)",
            borderWidth: 1.5,
            borderColor: theme.palette.error.main,
            color: theme.palette.error.main,
            "&:hover": {
              borderWidth: 1.5,
              borderColor: theme.palette.error.dark,
              backgroundColor: "rgba(239,68,68,0.08)",
            },
            "&:active": {
              borderColor: theme.palette.error.dark,
              backgroundColor: "rgba(239,68,68,0.12)",
            },
          }),
          outlinedSecondary: {
            color: "#4338ca",
            borderColor: "rgba(99,102,241,0.35)",
            backgroundColor: "rgba(99,102,241,0.06)",
            "&:hover": {
              borderColor: "rgba(99,102,241,0.55)",
              backgroundColor: "rgba(99,102,241,0.12)",
            },
            "&:active": {
              borderColor: "rgba(79,70,229,0.6)",
              backgroundColor: "rgba(79,70,229,0.16)",
            },
          },
          containedSecondary: {
            backgroundColor: "#eef2ff",
            color: "#4338ca",
            border: "1px solid rgba(99,102,241,0.25)",
            "&:hover": {
              backgroundColor: "#e0e7ff",
              borderColor: "rgba(99,102,241,0.35)",
            },
            "&:active": {
              backgroundColor: "#c7d2fe",
              borderColor: "rgba(79,70,229,0.45)",
            },
          },
          text: {
            padding: "0.5rem 1rem",
            color: "#4f46e5",
            "&:hover": {
              backgroundColor: "rgba(99,102,241,0.08)",
            },
          },
          startIcon: {
            marginRight: "0.65rem",
          },
          endIcon: {
            marginLeft: "0.65rem",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });
}

const theme = getTheme();

export default theme;
